/**
 * usePermission(point) — 与 admin-service requirePermission middleware 同源。
 *
 * 用法：
 *   const canApprove = usePermission(PERMISSIONS.REFUND_APPROVE);
 *   if (!canApprove) return null;
 *
 * 实现：
 *   - 订阅 useSession.role（zustand store）→ role 变更自动重新评估
 *   - 查 ROLE_PERMISSIONS[role] 是否含 point
 *   - role=null（未登录）→ false
 *   - role 不在预置角色 ENUM（ROLE_PERMISSIONS 的键）内 → false（防御性，与 middleware 行为一致）
 *
 * 注意：admin-web 端的 usePermission 是 UI 可视化辅助（隐藏按钮 / 路由 guard），
 * 真正权限校验在 admin-service requirePermission 中间件。前端跳过校验也不会越权。
 *
 * 跨 stream 接口契约 §6.4 / §6.5。
 */
import { useSession } from '../stores/session';
import { ROLE_PERMISSIONS } from '../contracts/roles';
import type { AdminRole } from '../contracts/roles';
import type { Permission } from '../contracts/permissions';

// 取 ROLE_PERMISSIONS 的键而不是再手写一份：新增角色必须先补矩阵（Record<AdminRole, …> 编译期穷举），
// 这里就不会漏——与 admin-service middleware/permission 同源同口径。
const KNOWN_ROLES: ReadonlySet<AdminRole> = new Set(Object.keys(ROLE_PERMISSIONS) as AdminRole[]);

export function usePermission(point: Permission): boolean {
  const role = useSession((s) => s.role);
  if (!role) return false;
  if (!KNOWN_ROLES.has(role as AdminRole)) return false;
  return ROLE_PERMISSIONS[role as AdminRole].includes(point);
}
